import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from "@angular/core";
import { NonNullableFormBuilder } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, Subscription, timer } from "rxjs";
import { distinctUntilChanged, filter, map, take } from "rxjs/operators";
import { ModalMaterialComponent } from "../../../../../shared/components/modal/modal-material.component";
import { VoicemailService } from "../../../../../../../common/services/api/resources/voicemail/voicemail.service";
import { SnackbarService } from "../../../../../shared/components/snackbar/snackbar.service";
import { AdvVoicemailboxWithExt } from "../../../admin-voicemail-types";
import { RecordingTypesEnum } from "@onsip/web/features/shared/components/togglableRecorder/config/recording-types.enum";
import { createAudioDurationValidator } from "@onsip/web/features/shared/validators/audio-duration.validator";
import { TogglableRecorderComponent } from "@onsip/web/features/shared/components/togglableRecorder/togglable-recorder.component";
import { SafeSrcPipe } from "@onsip/web/features/shared/pipes/safe-src.pipe";

@Component({
  selector: "onsip-admin-voicemail-greeting-edit",
  templateUrl: "./admin-voicemail-greeting-edit.component.html",
  styleUrls: ["./admin-voicemail-greeting-edit.component.scss"],
  standalone: false
})
export class AdminVoicemailGreetingEditComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(TogglableRecorderComponent) recorderRef!: TogglableRecorderComponent;
  @Input() voicemail!: AdvVoicemailboxWithExt;
  @Input() name!: string;
  @Input() id!: string;
  @Output() viewPanel = new EventEmitter<void>();

  greetingFormGroup = this.formbuilder.group({
    greeting: this.formbuilder.control(
      <File | undefined>undefined,
      [],
      [createAudioDurationValidator(this.domSanitize, 60)]
    )
  });

  greetingType: RecordingTypesEnum = RecordingTypesEnum.RECORD;
  showSaveButton = new BehaviorSubject<boolean>(true);
  loadingStateComplete$ = new BehaviorSubject(true);
  private originalUrl!: SafeResourceUrl;

  private unsubscriber = new Subscription();

  constructor(
    private formbuilder: NonNullableFormBuilder,
    private voicemailService: VoicemailService,
    private snackbar: SnackbarService,
    private translate: TranslateService,
    private dialog: MatDialog,
    private domSanitize: DomSanitizer,
    private safeSrc: SafeSrcPipe
  ) {}

  ngOnInit() {
    if (this.voicemail.voicemail.greetings) {
      const unavailableGreeting = Object.keys(this.voicemail.voicemail.greetings).find(
        greeting => greeting === "UnavailableGreeting"
      );
      if (unavailableGreeting) {
        // if there is a saved greeting, play this one in recording.
        const audioUrl = this.voicemailService.getVoicemailUrl(
          this.voicemail.advVoicemailboxId,
          unavailableGreeting
        );

        this.originalUrl = this.safeSrc.transform(audioUrl)!;
      }
    }

    this.trackFileDuration();
  }

  ngAfterViewInit(): void {
    if (this.voicemail.voicemail.greetings) {
      const unavailableGreeting = Object.keys(this.voicemail.voicemail.greetings).find(
        greeting => greeting === "UnavailableGreeting"
      );
      if (unavailableGreeting) {
        // to set fileName in upload section
        const fileName = this.voicemail.voicemail.greetings[unavailableGreeting].Path;
        this.recorderRef.recordingsForm.controls.upload.patchValue(new File([], fileName));
      }

      // to trigger change detection in audio recorder
      this.unsubscriber.add(
        timer(0).subscribe(() => {
          this.recorderRef.recordingUrlsByType$.next({
            ...this.recorderRef.recordingUrlsByType$.value,
            record: this.originalUrl,
            upload: this.originalUrl
          });
        })
      );
    }
    this.showSaveButton.next(!this.voicemail.voicemail.greetings);
    this.unsubscriber.add(
      this.recorderRef.recordingsForm.valueChanges
        .pipe(map(value => !value.upload && !value.record))
        .subscribe(value => this.showSaveButton.next(value))
    );
  }

  ngOnDestroy() {
    this.unsubscriber.unsubscribe();
  }

  async onSubmit(): Promise<void> {
    if (this.greetingFormGroup.valid) {
      this.loadingStateComplete$.next(false);
      if (this.greetingFormGroup.controls.greeting.value !== this.originalUrl) {
        const response = await this.voicemailService.advVoicemailboxGreetingUpload(
          "UnavailableGreeting",
          this.voicemail.advVoicemailboxId,
          this.greetingFormGroup.value.greeting
        );
        if (response.status === "error") {
          this.snackbar.openSnackBar(
            "To save, make sure file is wav format and less than 1 min long",
            "error"
          );
          this.voicemailService.clearErrors();
        } else {
          this.snackbar.openSnackBar(
            this.translate.instant("ONSIP_I18N.YOUR_CHANGES_HAVE_BEEN_SAVED"),
            "success"
          );
          this.viewPanel.emit();
        }
      } else {
        // close panel if there are no changes
        this.viewPanel.emit();
      }
      this.loadingStateComplete$.next(true);
    }
  }

  togglePanel(): void {
    this.unsubscriber.add(
      this.dialog
        .open(ModalMaterialComponent, {
          panelClass: ["mat-typography", "onsip-dialog-universal-style"],
          data: {
            title: this.translate.instant("ONSIP_I18N.CANCEL_EDITS"),
            message: this.translate.instant(
              "ONSIP_I18N.ANY_CHANGES_YOU_HAVE_MADE_WILL_NOT_BE_SAVED"
            ),
            primaryBtnText: this.translate.instant("ONSIP_I18N.OK"),
            secondaryBtnText: this.translate.instant("ONSIP_I18N.KEEP_EDITING"),
            primaryBtnFlat: true
          }
        })
        .afterClosed()
        .pipe(take(1))
        .subscribe(res => {
          if (res && res.doPrimaryAction) this.viewPanel.emit();
        })
    );
  }

  private trackFileDuration(): void {
    this.unsubscriber.add(
      this.greetingFormGroup.controls.greeting.statusChanges
        .pipe(
          map(() => this.greetingFormGroup.controls.greeting.hasError("tooLongDuration")),
          distinctUntilChanged(),
          filter(hasError => hasError)
        )
        .subscribe(() => {
          this.snackbar.openSnackBar(
            "To save, make sure the file is less than 1 min long",
            "error"
          );
          this.greetingFormGroup.controls.greeting.patchValue(undefined);
        })
    );
  }
}
